import {
  ColumnTable,
  OutlinedSelectOptionType,
  addToast,
  showDialogConfirm,
  OutlinedSelect,
  Icon,
} from '@octano/global-ui';
import { useState, useCallback } from 'react';
import { DegreeDocument } from '../../../types';
import {
  updateDegreeDocument,
  updateDegreeDocumentStatus,
} from '../../../requests';
import AzureFilesUploader from '../../../../../components/AzureFilesUploader/AzureFilesUploader';
import { SecureResponseFile } from '../../../../../components/AzureFilesUploader/types';

export function useColumns(
  studyPlanEnrollmentId: number,
  texts: {
    title: string;
    selectOptions: {
      rejected: string;
      approved: string;
      pending: string;
    };
    toasts: {
      upload: {
        success: string;
        failed: string;
      };
      remove: {
        success: string;
        failed: string;
      };
      editStatus: {
        success: string;
        failed: string;
      };
      confirmDelete: {
        title: string;
        description: string;
        confirm: string;
        cancel: string;
      };
    };
    table: {
      noResults: string;
      headers: {
        name: string;
        documentName: string;
        load: string;
        state: string;
      };
      values: {
        loadButton: string;
      };
    };
  },
  refresh: Function,
  disableEditing: boolean,
) {
  const options: OutlinedSelectOptionType[] = [
    {
      label: texts.selectOptions.rejected,
      value: 'rejected',
      icon: { name: 'error', color: 'danger' },
    },
    {
      label: texts.selectOptions.approved,
      value: 'approved',
      icon: { name: 'success', color: 'success' },
    },
  ];
  const [uploadingId, setUploadingId] = useState<number>();
  const [deletingId, setDeletingId] = useState<number>();
  const [updatingStatusId, setUpdatingStatusId] = useState<number>();

  const handleUpload = useCallback(
    async (document: DegreeDocument, fileId: number) => {
      if (uploadingId || deletingId || updatingStatusId) {
        return;
      }
      setUploadingId(document.id);
      const { error } = await updateDegreeDocument({
        studyPlanEnrollmentId,
        degreeDocumentId: document.id,
        fileId,
      });
      setUploadingId(undefined);
      if (!error) {
        !!refresh && refresh();
        addToast({
          color: 'success',
          icon: 'check',
          text: texts.toasts.upload.success,
        });
      } else {
        addToast({
          color: 'danger',
          icon: 'error',
          text: texts.toasts.upload.failed,
        });
      }
    },
    [
      uploadingId,
      deletingId,
      updatingStatusId,
      studyPlanEnrollmentId,
      refresh,
      texts,
    ],
  );

  const handleDelete = useCallback(
    async (document: DegreeDocument) => {
      if (uploadingId || deletingId || updatingStatusId) {
        return;
      }
      setDeletingId(document.id);
      const { error } = await updateDegreeDocument({
        studyPlanEnrollmentId,
        degreeDocumentId: document.id,
        fileId: null,
      });
      setDeletingId(undefined);
      if (!error) {
        !!refresh && refresh();
        addToast({
          color: 'success',
          icon: 'check',
          text: texts.toasts.remove.success,
        });
      } else {
        addToast({
          color: 'danger',
          icon: 'error',
          text: texts.toasts.remove.failed,
        });
      }
    },
    [
      uploadingId,
      deletingId,
      updatingStatusId,
      studyPlanEnrollmentId,
      refresh,
      texts,
    ],
  );

  const handleEditStatus = useCallback(
    async (
      document: DegreeDocument,
      status: 'approved' | 'rejected' | 'pending',
    ) => {
      if (uploadingId || deletingId || updatingStatusId) {
        return;
      }
      setUpdatingStatusId(document.id);
      const { error } = await updateDegreeDocumentStatus({
        studyPlanEnrollmentId,
        degreeDocumentId: document.id,
        status: status,
      });
      setUpdatingStatusId(undefined);
      if (!error) {
        !!refresh && refresh();
        addToast({
          color: 'success',
          icon: 'check',
          text: texts.toasts.editStatus.success,
        });
      } else {
        addToast({
          color: 'danger',
          icon: 'error',
          text: texts.toasts.editStatus.failed,
        });
      }
    },
    [
      uploadingId,
      deletingId,
      updatingStatusId,
      studyPlanEnrollmentId,
      refresh,
      texts,
    ],
  );

  const handleRequestDelete = useCallback(
    async (document: DegreeDocument) => {
      if (uploadingId || deletingId) {
        return;
      }
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: texts.toasts.confirmDelete.title,
        subtitle: texts.toasts.confirmDelete.description,
        btnConfirm: {
          text: texts.toasts.confirmDelete.confirm,
          onConfirm: () => handleDelete(document),
        },
        btnClose: {
          text: texts.toasts.confirmDelete.cancel,
        },
      });
    },
    [uploadingId, deletingId, handleDelete, texts],
  );

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }
    window.open(file?.url, '_blank');
  }, []);

  const columns: ColumnTable<DegreeDocument>[] = [
    {
      headerText: texts.table.headers.name,
      columnName: 'name',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      width: 250,
    },
    {
      headerText: 'Nombre',
      columnName: texts.table.headers.documentName,
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => {
        return row?.file?.url ? (
          <div>
            {`${row?.file?.name}.${row?.file?.extension}`}
            <button
              className="btn btn-sm ml-2"
              type="button"
              onClick={() => handleDownloadFile(row.file!)}
            >
              <Icon name="download" color="primary" />
            </button>
            <button
              className="btn btn-sm ml-2"
              type="button"
              onClick={() =>
                row.updateAllowed ? handleRequestDelete(row) : undefined
              }
            >
              <Icon
                name="trash"
                color={row.updateAllowed ? 'primary' : 'disabled'}
              />
            </button>
          </div>
        ) : (
          <></>
        );
      },
    },
    {
      headerText: texts.table.headers.load,
      columnName: 'load',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      width: 170,
      cellFormat: ({ row }) => {
        return (
          <div style={{ width: 170 }}>
            <AzureFilesUploader
              label=" "
              btnText={texts.table.values.loadButton}
              disabled={!row.updateAllowed || disableEditing}
              onUploaded={(next) => handleUpload(row, next.id)}
            />
          </div>
        );
      },
    },
    {
      headerText: 'Estado',
      columnName: texts.table.headers.state,
      thClassName: 'text-center',
      tdClassName: 'text-center',
      width: 200,
      cellFormat: ({ row }) => {
        return (
          <div style={{ marginBottom: '-2rem' }}>
            <OutlinedSelect
              disabled={!row.file || !row.updateAllowed || disableEditing}
              name="select"
              isClearable={false}
              isSearchable={false}
              value={
                options.find((element) => element.value === row.status) || {
                  label: 'Sin revisión',
                  value: 'pending',
                  icon: { name: 'warning', color: 'warning' },
                }
              }
              onChange={(e) => handleEditStatus(row, e.value)}
              options={options}
            />
          </div>
        );
      },
    },
  ];
  return columns;
}
